import { useContext, useEffect, useState } from "react";
import { ChartContext } from "../../../Context/ChartContext/ChartContext";
import MainContext from "../../../Context/MainContext/MainContext";

import MUIButton from "@mui/material/Button";
import MUIGrid from "@mui/material/Grid2";
import MUIBox from "@mui/material/Box";
import MUIAlert from "@mui/material/Alert";
import MUIAlertTitle from "@mui/material/AlertTitle";
import MUICheckbox from "@mui/material/Checkbox";
import MUIFormControlLabel from "@mui/material/FormControlLabel";
import MUITextField from "@mui/material/TextField";
import MUILocationOnIcon from "@mui/icons-material/LocationOn";
import MUITypography from "@mui/material/Typography";
import MUIStack from "@mui/material/Stack";
import MUIAutocomplete from "@mui/material/Autocomplete";
import MUISmartphoneIcon from "@mui/icons-material/Smartphone";
import MUIContactsIcon from "@mui/icons-material/Contacts";
import MUIEmailIcon from "@mui/icons-material/Email";
import MUIPersonIcon from "@mui/icons-material/Person";
import MUIStarIcon from "@mui/icons-material/Star";

import Card from "../../Card";
import InfoItem from "../../InfoItem";
import Term from "../../../Utility/Crud/Term";
import Format from "../../../Utility/Format";

/**
 * CardChartContact renders a Card component
 * displaying patient contact information.
 * @returns {JSX.Element}
 */

const title = "Contact";
const icon = MUIContactsIcon;

const className = "CardChartContact";

export default function CardChartContact(props) {
  const chartContext = useContext(ChartContext);
  const mainContext = useContext(MainContext);
  const patient = chartContext.chart.patient[chartContext.patientId];
  const originalPatient =
    chartContext.originalChart.patient[chartContext.patientId];
  const [editMode, setEditMode] = useState(false);
  const [cardChart, setCardChart] = useState(null);

  const showEditButton =
    props.showEditButton !== undefined ? props.showEditButton : true;
  const shouldShowChangesAlert =
    props.shouldShowChangesAlert !== undefined
      ? props.shouldShowChangesAlert
      : true;

  useEffect(() => {
    if (editMode) {
      chartContext.updateValidationRules({
        key: className,
        rules: [
          {
            target: "patient",
            id: chartContext.patientId,
            field: "address",
            rule: {
              required: true,
            },
          },
          {
            target: "patient",
            id: chartContext.patientId,
            field: "city",
            rule: {
              required: true,
            },
          },
          {
            target: "patient",
            id: chartContext.patientId,
            field: "state",
            rule: {
              required: true,
            },
          },
          {
            target: "patient",
            id: chartContext.patientId,
            field: "zipCode",
            rule: {
              required: true,
              type: "zipCode",
            },
          },
          {
            target: "patient",
            id: chartContext.patientId,
            field: "phone",
            rule: {
              required: true,
              type: "phone",
            },
          },
          {
            target: "patient",
            id: chartContext.patientId,
            field: "alternativePhone",
            rule: {
              required: false,
              type: "phone",
            },
          },
          {
            target: "patient",
            id: chartContext.patientId,
            field: "emergencyContactPhone",
            rule: {
              required: false,
              type: "phone",
            },
          },
          {
            target: "patient",
            id: chartContext.patientId,
            field: "email",
            rule: {
              required: false,
              type: "email",
            },
          },
        ],
      });
    } else {
      chartContext.updateValidationRules({ key: className, rules: [] });
    }
  }, [editMode]);

  // Close edit mode if originalChart changes (on save)
  useEffect(() => {
    setEditMode(false);
  }, [chartContext.originalChart]);

  // Sync this card's edit mode with ChartContext for ChartNavigationBar display
  useEffect(() => {
    chartContext.setCardEditMode?.(className, editMode);
    return () => chartContext.setCardEditMode?.(className, false);
  }, [editMode, chartContext.setCardEditMode]);

  /**
   * Returns the styles for a container that should be full width at a given max width.
   * Only used when sidebar=true to handle responsive container queries.
   * @param {number} maxWidth - The max width at which the container should be full width.
   * @returns {Object} The styles for the container.
   */
  const getContainerQuery = (maxWidth) => {
    if (!props.sidebar) {
      return {};
    }

    return {
      [`@container (max-width: ${maxWidth}px)`]: {
        flexBasis: "100% !important",
        maxWidth: "100% !important",
      },
    };
  };

  /**
   * Renders the address section of the patient contact information.
   * @returns {JSX.Element} Grid layout with address information display
   */
  const renderAddressSection = () => {
    const getStateListOptions = () => {
      return [
        { value: "AL", label: "AL" },
        { value: "AK", label: "AK" },
        { value: "AZ", label: "AZ" },
        { value: "AR", label: "AR" },
        { value: "CA", label: "CA" },
        { value: "CO", label: "CO" },
        { value: "CT", label: "CT" },
        { value: "DE", label: "DE" },
        { value: "FL", label: "FL" },
        { value: "GA", label: "GA" },
        { value: "HI", label: "HI" },
        { value: "ID", label: "ID" },
        { value: "IL", label: "IL" },
        { value: "IN", label: "IN" },
        { value: "IA", label: "IA" },
        { value: "KS", label: "KS" },
        { value: "KY", label: "KY" },
        { value: "LA", label: "LA" },
        { value: "ME", label: "ME" },
        { value: "MD", label: "MD" },
        { value: "MA", label: "MA" },
        { value: "MI", label: "MI" },
        { value: "MN", label: "MN" },
        { value: "MS", label: "MS" },
        { value: "MO", label: "MO" },
        { value: "MT", label: "MT" },
        { value: "NE", label: "NE" },
        { value: "NV", label: "NV" },
        { value: "NH", label: "NH" },
        { value: "NJ", label: "NJ" },
        { value: "NM", label: "NM" },
        { value: "NY", label: "NY" },
        { value: "NC", label: "NC" },
        { value: "ND", label: "ND" },
        { value: "OH", label: "OH" },
        { value: "OK", label: "OK" },
        { value: "OR", label: "OR" },
        { value: "PA", label: "PA" },
        { value: "RI", label: "RI" },
        { value: "SC", label: "SC" },
        { value: "SD", label: "SD" },
        { value: "TN", label: "TN" },
        { value: "TX", label: "TX" },
        { value: "UT", label: "UT" },
        { value: "VT", label: "VT" },
        { value: "VA", label: "VA" },
        { value: "WA", label: "WA" },
        { value: "WV", label: "WV" },
        { value: "WI", label: "WI" },
        { value: "WY", label: "WY" },
        { value: "DC", label: "DC" },
      ];
    };

    return (
      <MUIGrid
        sx={getContainerQuery(600)}
        size={{
          lg: 5,
          md: 12,
          xs: 12,
        }}
      >
        <MUIGrid container spacing={2}>
          <MUIGrid size={12}>
            <MUIStack
              direction="row"
              spacing={1}
              sx={{ display: "flex", alignItems: "center" }}
            >
              <MUILocationOnIcon
                fontSize="medium"
                sx={{ color: (theme) => theme.palette.secondary.main }}
              />
              <MUITypography variant="subtitle2">Address</MUITypography>
            </MUIStack>
          </MUIGrid>
          <MUIGrid
            sx={getContainerQuery(600)}
            size={{
              lg: 12,
              md: 6,
              xs: 12,
            }}
          >
            <MUITextField
              autoFocus={true}
              value={patient && patient.address ? patient.address : ""}
              size="small"
              label="Address"
              variant="standard"
              fullWidth={true}
              onBlur={(e) => {
                chartContext.onChange({
                  patient: {
                    [chartContext.patientId]: {
                      address: e.target.value.trim(),
                    },
                  },
                });
                setCardChart({ ...cardChart, address: e.target.value.trim() });
              }}
              onChange={(e) => {
                chartContext.onChange({
                  patient: {
                    [chartContext.patientId]: { address: e.target.value },
                  },
                });
                setCardChart({ ...cardChart, address: e.target.value });
              }}
              required={true}
              error={
                chartContext.validationIssues[className]?.patient?.[
                  chartContext.patientId
                ].address
                  ? true
                  : false
              }
              helperText={
                chartContext.validationIssues[className]?.patient?.[
                  chartContext.patientId
                ].address
                  ? chartContext.validationIssues[className]?.patient?.[
                      chartContext.patientId
                    ].address
                  : ""
              }
              slotProps={{
                htmlInput: {
                  maxLength: 255,
                },

                inputLabel: { shrink: true },
              }}
            />
          </MUIGrid>
          <MUIGrid
            sx={getContainerQuery(600)}
            size={{
              lg: 5,
              md: 6,
              xs: 12,
            }}
          >
            <MUITextField
              value={patient && patient.city ? patient.city : ""}
              size="small"
              label="City"
              variant="standard"
              fullWidth={true}
              onBlur={(e) =>
                chartContext.onChange({
                  patient: {
                    [chartContext.patientId]: { city: e.target.value.trim() },
                  },
                })
              }
              onChange={(e) => {
                chartContext.onChange({
                  patient: {
                    [chartContext.patientId]: { city: e.target.value },
                  },
                });
                setCardChart({ ...cardChart, city: e.target.value });
              }}
              required={true}
              error={
                chartContext.validationIssues[className]?.patient?.[
                  chartContext.patientId
                ].city
                  ? true
                  : false
              }
              helperText={
                chartContext.validationIssues[className]?.patient?.[
                  chartContext.patientId
                ].city
                  ? chartContext.validationIssues[className]?.patient?.[
                      chartContext.patientId
                    ].city
                  : ""
              }
              slotProps={{
                htmlInput: {
                  maxLength: 255,
                },

                inputLabel: { shrink: true },
              }}
            />
          </MUIGrid>
          <MUIGrid
            sx={getContainerQuery(600)}
            size={{
              lg: 3,
              md: 6,
              xs: 12,
            }}
          >
            <MUIAutocomplete
              options={getStateListOptions()}
              value={
                getStateListOptions().find(
                  (option) => option.value === patient.state,
                ) || null
              }
              isOptionEqualToValue={(option, value) =>
                option?.value === value?.value
              }
              getOptionLabel={(option) => option?.label || ""}
              onChange={(e, newValue) => {
                chartContext.onChange({
                  patient: {
                    [chartContext.patientId]: {
                      state: newValue?.value || null,
                    },
                  },
                });
                setCardChart({ ...cardChart, state: newValue?.value || null });
              }}
              renderInput={(params) => (
                <MUITextField
                  {...params}
                  size="small"
                  variant="standard"
                  label="State"
                  fullWidth
                  required={true}
                  error={
                    chartContext.validationIssues[className]?.patient?.[
                      chartContext.patientId
                    ].state
                      ? true
                      : false
                  }
                  helperText={
                    chartContext.validationIssues[className]?.patient?.[
                      chartContext.patientId
                    ].state
                      ? chartContext.validationIssues[className]?.patient?.[
                          chartContext.patientId
                        ].state
                      : ""
                  }
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </MUIGrid>
          <MUIGrid
            sx={getContainerQuery(600)}
            size={{
              lg: 4,
              md: 6,
              xs: 12,
            }}
          >
            <MUITextField
              value={patient && patient.zipCode ? patient.zipCode : ""}
              size="small"
              label="Postal Code"
              variant="standard"
              fullWidth={true}
              onBlur={(e) =>
                chartContext.onChange({
                  patient: {
                    [chartContext.patientId]: {
                      zipCode: e.target.value.trim(),
                    },
                  },
                })
              }
              onChange={(e) => {
                chartContext.onChange({
                  patient: {
                    [chartContext.patientId]: { zipCode: e.target.value },
                  },
                });
                setCardChart({ ...cardChart, zipCode: e.target.value });
              }}
              required={true}
              error={
                chartContext.validationIssues[className]?.patient?.[
                  chartContext.patientId
                ].zipCode
                  ? true
                  : false
              }
              helperText={
                chartContext.validationIssues[className]?.patient?.[
                  chartContext.patientId
                ].zipCode
                  ? chartContext.validationIssues[className]?.patient?.[
                      chartContext.patientId
                    ].zipCode
                  : ""
              }
              slotProps={{
                htmlInput: {
                  maxLength: 255,
                },

                inputLabel: { shrink: true },
              }}
            />
          </MUIGrid>
        </MUIGrid>
      </MUIGrid>
    );
  };

  /**
   * Renders the phone section of the patient contact information.
   * @returns {JSX.Element} Grid layout with phone information display
   */
  const renderPhoneSection = () => {
    return (
      <MUIGrid
        sx={getContainerQuery(600)}
        size={{
          lg: 3,
          md: 12,
          xs: 12,
        }}
      >
        <MUIGrid container spacing={2}>
          <MUIGrid size={12}>
            <MUIStack
              direction="row"
              spacing={1}
              sx={{ display: "flex", alignItems: "center" }}
            >
              <MUISmartphoneIcon
                fontSize="medium"
                sx={{ color: (theme) => theme.palette.secondary.main }}
              />
              <MUITypography variant="subtitle2">Phone</MUITypography>
            </MUIStack>
          </MUIGrid>
          <MUIGrid
            sx={getContainerQuery(600)}
            size={{
              lg: 12,
              md: 6,
              xs: 12,
            }}
          >
            <MUITextField
              value={
                patient && patient.phone
                  ? Format.phone(patient.phone, true)
                  : ""
              }
              size="small"
              label="Primary Phone"
              variant="standard"
              fullWidth={true}
              onBlur={(e) => {
                const cleaned =
                  e.target.value.replace(/\D/g, "").slice(0, 10) || null;
                chartContext.onChange({
                  patient: { [chartContext.patientId]: { phone: cleaned } },
                });
              }}
              onChange={(e) => {
                const cleaned = e.target.value.replace(/\D/g, "").slice(0, 10);
                chartContext.onChange({
                  patient: { [chartContext.patientId]: { phone: cleaned } },
                });
                setCardChart({ ...cardChart, phone: cleaned });
              }}
              required={true}
              error={
                chartContext.validationIssues[className]?.patient?.[
                  chartContext.patientId
                ].phone
                  ? true
                  : false
              }
              helperText={
                chartContext.validationIssues[className]?.patient?.[
                  chartContext.patientId
                ].phone
                  ? chartContext.validationIssues[className]?.patient?.[
                      chartContext.patientId
                    ].phone
                  : ""
              }
              slotProps={{
                htmlInput: {
                  maxLength: 14,
                },

                inputLabel: { shrink: true },
              }}
            />
          </MUIGrid>

          <MUIGrid
            sx={getContainerQuery(600)}
            size={{
              lg: 12,
              md: 6,
              xs: 12,
            }}
          >
            <MUIFormControlLabel
              control={
                <MUICheckbox
                  checked={!!patient?.smsMessages}
                  onChange={(e) => {
                    chartContext.onChange({
                      patient: {
                        [chartContext.patientId]: {
                          ...patient,
                          smsMessages: e.target.checked,
                        },
                      },
                    });
                    setCardChart({
                      ...cardChart,
                      smsMessages: e.target.checked,
                    });
                  }}
                />
              }
              label="Enroll in order update texts"
            />
          </MUIGrid>

          <MUIGrid
            sx={getContainerQuery(600)}
            size={{
              lg: 12,
              md: 6,
              xs: 12,
            }}
          >
            <MUITextField
              value={
                patient && patient.alternativePhone
                  ? Format.phone(patient.alternativePhone, true)
                  : ""
              }
              size="small"
              label="Secondary Phone"
              variant="standard"
              fullWidth={true}
              onBlur={(e) => {
                const cleaned =
                  e.target.value.replace(/\D/g, "").slice(0, 10) || null;
                chartContext.onChange({
                  patient: {
                    [chartContext.patientId]: { alternativePhone: cleaned },
                  },
                });
              }}
              onChange={(e) => {
                const cleaned = e.target.value.replace(/\D/g, "").slice(0, 10);
                chartContext.onChange({
                  patient: {
                    [chartContext.patientId]: { alternativePhone: cleaned },
                  },
                });
                setCardChart({ ...cardChart, alternativePhone: cleaned });
              }}
              error={
                chartContext.validationIssues[className]?.patient?.[
                  chartContext.patientId
                ].alternativePhone
                  ? true
                  : false
              }
              helperText={
                chartContext.validationIssues[className]?.patient?.[
                  chartContext.patientId
                ].alternativePhone
                  ? chartContext.validationIssues?.[className]?.patient?.[
                      chartContext.patientId
                    ].alternativePhone
                  : ""
              }
              slotProps={{
                htmlInput: {
                  maxLength: 14,
                },

                inputLabel: { shrink: true },
              }}
            />
          </MUIGrid>
        </MUIGrid>
      </MUIGrid>
    );
  };

  /**
   * Renders the email section of the patient contact information.
   * @returns {JSX.Element} Grid layout with email information display
   */
  const renderEmailSection = () => {
    return (
      <MUIGrid
        sx={getContainerQuery(600)}
        size={{
          lg: 4,
          md: 12,
          xs: 12,
        }}
      >
        <MUIGrid container spacing={2}>
          <MUIGrid size={12}>
            <MUIStack
              direction="row"
              spacing={1}
              sx={{ display: "flex", alignItems: "center" }}
            >
              <MUIEmailIcon
                fontSize="medium"
                sx={{ color: (theme) => theme.palette.secondary.main }}
              />
              <MUITypography variant="subtitle2">Email</MUITypography>
            </MUIStack>
          </MUIGrid>
          <MUIGrid
            sx={getContainerQuery(600)}
            size={{
              lg: 12,
              md: 6,
              xs: 12,
            }}
          >
            <MUITextField
              value={patient && patient.email ? patient.email : ""}
              size="small"
              label="Email"
              variant="standard"
              fullWidth={true}
              onBlur={(e) => {
                const cleaned = e.target.value ? e.target.value.trim() : null;
                chartContext.onChange({
                  patient: { [chartContext.patientId]: { email: cleaned } },
                });
              }}
              onChange={(e) => {
                let value = e.target.value ? e.target.value.trim() : "";
                chartContext.onChange({
                  patient: { [chartContext.patientId]: { email: value } },
                });
                setCardChart({ ...cardChart, email: value });
              }}
              error={
                chartContext.validationIssues[className]?.patient?.[
                  chartContext.patientId
                ].email
                  ? true
                  : false
              }
              helperText={
                chartContext.validationIssues[className]?.patient?.[
                  chartContext.patientId
                ].email
                  ? chartContext.validationIssues[className]?.patient?.[
                      chartContext.patientId
                    ].email
                  : ""
              }
              slotProps={{
                htmlInput: {
                  maxLength: 255,
                },

                inputLabel: { shrink: true },
              }}
            />
          </MUIGrid>
        </MUIGrid>
      </MUIGrid>
    );
  };

  /**
   * Renders the emergency contact section of the patient contact information.
   * @returns {JSX.Element} Grid layout with emergency contact information display
   */
  const renderEmergencyContactSection = () => {
    const getEmergencyRelationshipOptions = () => {
      return [
        { value: "Spouse/Partner", label: "Spouse/Partner" },
        { value: "Sibling", label: "Sibling" },
        { value: "Child", label: "Child" },
        { value: "Parent", label: "Parent" },
        { value: "Aunt/Uncle", label: "Aunt/Uncle" },
        { value: "Other", label: "Other" },
      ];
    };

    return (
      <MUIGrid
        sx={getContainerQuery(600)}
        size={{
          lg: 5,
          md: 12,
          xs: 12,
        }}
      >
        <MUIGrid container spacing={2}>
          <MUIGrid size={12}>
            <MUIStack
              direction="row"
              spacing={1}
              sx={{ display: "flex", alignItems: "center" }}
            >
              <MUIPersonIcon
                fontSize="medium"
                sx={{ color: (theme) => theme.palette.secondary.main }}
              />
              <MUITypography variant="subtitle2">
                Emergency Contact
              </MUITypography>
            </MUIStack>
          </MUIGrid>
          <MUIGrid
            sx={getContainerQuery(600)}
            size={{
              lg: 12,
              md: 6,
              xs: 12,
            }}
          >
            <MUITextField
              value={
                patient && patient.emergencyContact
                  ? patient.emergencyContact
                  : ""
              }
              size="small"
              label="Name"
              variant="standard"
              fullWidth={true}
              onBlur={(e) =>
                chartContext.onChange({
                  patient: {
                    [chartContext.patientId]: {
                      emergencyContact: e.target.value.trim(),
                    },
                  },
                })
              }
              onChange={(e) => {
                chartContext.onChange({
                  patient: {
                    [chartContext.patientId]: {
                      emergencyContact: e.target.value,
                    },
                  },
                });
                setCardChart({
                  ...cardChart,
                  emergencyContact: e.target.value,
                });
              }}
              slotProps={{
                htmlInput: {
                  maxLength: 255,
                },

                inputLabel: { shrink: true },
              }}
            />
          </MUIGrid>
          <MUIGrid
            sx={getContainerQuery(600)}
            size={{
              lg: 6,
              md: 6,
              xs: 12,
            }}
          >
            <MUIAutocomplete
              options={getEmergencyRelationshipOptions()}
              value={
                getEmergencyRelationshipOptions().find(
                  (option) => option.value === patient.emergencyRelationship,
                ) || null
              }
              isOptionEqualToValue={(option, value) =>
                option?.value === value?.value
              }
              getOptionLabel={(option) => option?.label || ""}
              onChange={(e, newValue) => {
                chartContext.onChange({
                  patient: {
                    [chartContext.patientId]: {
                      emergencyRelationship: newValue?.value || null,
                    },
                  },
                });
                setCardChart({
                  ...cardChart,
                  emergencyRelationship: newValue?.value || null,
                });
              }}
              renderInput={(params) => (
                <MUITextField
                  {...params}
                  size="small"
                  variant="standard"
                  label="Relationship"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </MUIGrid>
          <MUIGrid
            sx={getContainerQuery(600)}
            size={{
              lg: 6,
              md: 6,
              xs: 12,
            }}
          >
            <MUITextField
              value={
                patient && patient.emergencyContactPhone
                  ? Format.phone(patient.emergencyContactPhone, true)
                  : ""
              }
              size="small"
              label="Phone"
              variant="standard"
              fullWidth={true}
              onBlur={(e) => {
                const cleaned =
                  e.target.value.replace(/\D/g, "").slice(0, 10) || null;
                chartContext.onChange({
                  patient: {
                    [chartContext.patientId]: {
                      emergencyContactPhone: cleaned,
                    },
                  },
                });
              }}
              onChange={(e) => {
                const cleaned = e.target.value.replace(/\D/g, "").slice(0, 10);
                chartContext.onChange({
                  patient: {
                    [chartContext.patientId]: {
                      emergencyContactPhone: cleaned,
                    },
                  },
                });
                setCardChart({ ...cardChart, emergencyContactPhone: cleaned });
              }}
              error={
                chartContext.validationIssues[className]?.patient?.[
                  chartContext.patientId
                ].emergencyContactPhone
                  ? true
                  : false
              }
              helperText={
                chartContext.validationIssues[className]?.patient?.[
                  chartContext.patientId
                ].emergencyContactPhone
                  ? chartContext.validationIssues[className]?.patient?.[
                      chartContext.patientId
                    ].emergencyContactPhone
                  : ""
              }
              slotProps={{
                htmlInput: {
                  maxLength: 14,
                },

                inputLabel: { shrink: true },
              }}
            />
          </MUIGrid>
        </MUIGrid>
      </MUIGrid>
    );
  };

  /**
   * Renders the marketing section of the patient contact information.
   * @returns {JSX.Element} Grid layout with marketing information display
   */
  const renderMarketingSection = () => {
    const getMarketingPreferredContactMethodList = () => {
      return Object.values(mainContext.termsIndexed)
        .filter((term) => term.type === "marketing_preferred_contact_method")
        .map((term) => ({
          value: term.id,
          label: term.name,
        }));
    };

    return (
      <MUIGrid
        sx={getContainerQuery(600)}
        size={{
          lg: 3,
          md: 12,
          xs: 12,
        }}
      >
        <MUIGrid container spacing={2}>
          <MUIGrid size={12}>
            <MUIStack
              direction="row"
              spacing={1}
              sx={{ display: "flex", alignItems: "center" }}
            >
              <MUIStarIcon
                fontSize="medium"
                sx={{ color: (theme) => theme.palette.secondary.main }}
              />
              <MUITypography variant="subtitle2">Marketing</MUITypography>
            </MUIStack>
          </MUIGrid>
          <MUIGrid
            sx={getContainerQuery(600)}
            size={{
              lg: 12,
              md: 6,
              xs: 12,
            }}
          >
            <MUIAutocomplete
              options={getMarketingPreferredContactMethodList()}
              value={
                getMarketingPreferredContactMethodList().find(
                  (option) =>
                    option.value === parseInt(patient.preferredContactMethod),
                ) || null
              }
              isOptionEqualToValue={(option, value) =>
                option?.value === value?.value
              }
              getOptionLabel={(option) => option?.label || ""}
              onChange={(e, newValue) => {
                chartContext.onChange({
                  patient: {
                    [chartContext.patientId]: {
                      preferredContactMethod: newValue?.value || null,
                    },
                  },
                });
                setCardChart({
                  ...cardChart,
                  preferredContactMethod: newValue?.value || null,
                });
              }}
              renderInput={(params) => (
                <MUITextField
                  {...params}
                  size="small"
                  variant="standard"
                  label="Preferred Contact Method"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </MUIGrid>
          <MUIGrid
            sx={getContainerQuery(600)}
            size={{
              lg: 12,
              md: 6,
              xs: 12,
            }}
          >
            <MUIFormControlLabel
              control={
                <MUICheckbox
                  checked={patient?.marketingDoNotContact || false}
                  onChange={(e) =>
                    chartContext.onChange({
                      patient: {
                        [chartContext.patientId]: {
                          marketingDoNotContact: e.target.checked,
                        },
                      },
                    })
                  }
                  size="small"
                />
              }
              label="Do not contact"
            />
          </MUIGrid>
        </MUIGrid>
      </MUIGrid>
    );
  };

  /**
   * Renders the contact changes alert when patient differs from originalPatient.
   * Shows exactly which fields changed from what to what, matching CardChartInsurance pattern.
   * @returns {JSX.Element|null} The changes alert or null if no changes
   */
  const renderContactChangesAlert = () => {
    if (!shouldShowChangesAlert) {
      return null;
    }
    const contactFields = [
      {
        key: "address",
        label: "Address",
        formatter: (value) => value || "[None]",
      },
      { key: "city", label: "City", formatter: (value) => value || "[None]" },
      { key: "state", label: "State", formatter: (value) => value || "[None]" },
      {
        key: "zipCode",
        label: "Postal Code",
        formatter: (value) => value || "[None]",
      },
    ];

    const contactChanges = [];
    contactFields.forEach((field) => {
      const currentValue = patient?.[field.key];
      const originalValue = originalPatient?.[field.key];
      if (currentValue !== originalValue) {
        contactChanges.push({
          label: field.label,
          before: field.formatter(originalValue),
          after: field.formatter(currentValue),
        });
      }
    });

    if (contactChanges.length === 0) {
      return null;
    }

    return (
      <MUIAlert severity="info" sx={{ mb: 3, width: "100%" }}>
        <MUIAlertTitle>Contact Changes</MUIAlertTitle>
        <MUIBox component="ul" sx={{ pl: 2, mb: 0 }}>
          {contactChanges.map((change, index) => (
            <MUITypography component="li" variant="body2" key={index}>
              <strong>{change.label}:</strong> {change.before} → {change.after}
            </MUITypography>
          ))}
        </MUIBox>
      </MUIAlert>
    );
  };

  /**
   * Renders the edit view displaying patient contact information.
   * @returns {JSX.Element} Grid layout with patient information display
   */
  const renderEditView = () => {
    return (
      <MUIGrid
        container
        spacing={4}
        rowSpacing={4}
        sx={{ containerType: "inline-size" }}
      >
        {renderAddressSection()}
        {renderPhoneSection()}
        {renderEmailSection()}
        {renderEmergencyContactSection()}
        {renderMarketingSection()}
      </MUIGrid>
    );
  };

  /**
   * Renders the read-only view displaying patient demographic information.
   * @returns {JSX.Element} Grid layout with patient information display
   */
  const renderReadOnlyView = () => {
    return (
      <MUIGrid container spacing={props.sidebar ? 0 : 4} rowSpacing={2}>
        <MUIGrid
          sx={{ display: "flex" }}
          size={{
            xs: props.sidebar ? 6 : "auto",
            sm: props.sidebar ? 12 : "auto",
            md: props.sidebar ? 6 : "auto",
          }}
        >
          <InfoItem
            icon={{
              icon: "LocationOn",
              color: (theme) => theme.palette.secondary.main,
            }}
            label={"Address"}
            value={Format.address(
              patient.address,
              patient.city,
              patient.state,
              patient.zipCode,
            )}
          />
        </MUIGrid>
        <MUIGrid
          sx={{ display: "flex" }}
          size={{
            xs: props.sidebar ? 6 : "auto",
            sm: props.sidebar ? 12 : "auto",
            md: props.sidebar ? 6 : "auto",
          }}
        >
          <InfoItem
            icon={{
              icon: "Smartphone",
              color: (theme) => theme.palette.secondary.main,
            }}
            label={"Phone"}
            value={
              <>
                {Format.phone(patient.phone, true)}
                {Format.phone(patient.alternativePhone, true).trim() !== "" && (
                  <>
                    <br />
                    {Format.phone(patient.alternativePhone, true)}
                  </>
                )}
                {patient.smsMessages && (
                  <>
                    <br />✓ Order update texts
                  </>
                )}
              </>
            }
          />
        </MUIGrid>
        <MUIGrid
          sx={{ display: "flex" }}
          size={{
            xs: props.sidebar ? 6 : "auto",
            sm: props.sidebar ? 12 : "auto",
            md: props.sidebar ? 6 : "auto",
          }}
        >
          <InfoItem
            icon={{
              icon: "Email",
              color: (theme) => theme.palette.secondary.main,
            }}
            label={"Email"}
            value={patient.email}
          />
        </MUIGrid>
        {patient.emergencyContact || patient.emergencyContactPhone ? (
          <MUIGrid
            sx={{ display: "flex" }}
            size={{
              xs: props.sidebar ? 6 : "auto",
              sm: props.sidebar ? 12 : "auto",
              md: props.sidebar ? 6 : "auto",
            }}
          >
            <InfoItem
              icon={{
                icon: "Person",
                color: (theme) => theme.palette.secondary.main,
              }}
              label={"Emergency Contact"}
              value={
                <>
                  {patient.emergencyContact && (
                    <>
                      {patient.emergencyContact}
                      {patient.emergencyRelationship && (
                        <> • {patient.emergencyRelationship}</>
                      )}
                      <br />
                    </>
                  )}
                  {patient.emergencyContactPhone &&
                    `${Format.phone(patient.emergencyContactPhone, true)}`}
                </>
              }
            />
          </MUIGrid>
        ) : null}
        {patient.marketingDoNotContact || patient.preferredContactMethod ? (
          <MUIGrid
            sx={{ display: "flex" }}
            size={{
              xs: props.sidebar ? 6 : "auto",
              sm: props.sidebar ? 12 : "auto",
              md: props.sidebar ? 6 : "auto",
            }}
          >
            <InfoItem
              icon={{
                icon: "Star",
                color: (theme) => theme.palette.secondary.main,
              }}
              label={"Marketing"}
              value={
                <>
                  {patient.preferredContactMethod ? (
                    <>
                      {"Preferred: " +
                        Term.getDisplayName(
                          mainContext.termsIndexed[
                            patient.preferredContactMethod
                          ],
                        )}
                      {patient.marketingDoNotContact && (
                        <MUIBox
                          component="span"
                          sx={{ color: (theme) => theme.palette.error.main }}
                        >
                          <br />
                          Do not contact
                        </MUIBox>
                      )}
                    </>
                  ) : patient.marketingDoNotContact ? (
                    <MUIBox
                      component="span"
                      sx={{ color: (theme) => theme.palette.error.main }}
                    >
                      Do not contact
                    </MUIBox>
                  ) : null}
                </>
              }
            />
          </MUIGrid>
        ) : null}
      </MUIGrid>
    );
  };
  /**
   * Determines which view to render based on edit mode state.
   * @returns {JSX.Element} Either edit view or read-only view
   */
  const renderContent = () => {
    if (editMode) {
      return renderEditView();
    }
    return (
      <>
        {renderContactChangesAlert()}
        {renderReadOnlyView()}
      </>
    );
  };

  /**
   * Returns the action buttons for the card component.
   * @returns {Array} Array of action button components
   */
  const getActions = () => {
    const handleCancel = () => {
      const updatedPatient = { ...patient };
      const contactFields = cardChart ? Object.keys(cardChart) : [];

      for (const field of contactFields) {
        if (patient[field] !== originalPatient[field]) {
          updatedPatient[field] = originalPatient[field];
        }
      }
      chartContext.onChange({
        patient: { [chartContext.patientId]: { ...updatedPatient } },
      });
      chartContext.setCardEditMode?.(className, false);
      setEditMode(false);

      const clearedIssues = { ...chartContext.validationIssues };
      delete clearedIssues[className];
      chartContext.updateValidationIssues(clearedIssues);
    };

    return [
      ...(editMode
        ? [
            <MUIButton
              color="error"
              disabled={chartContext.isUpdating}
              onClick={handleCancel}
              size="small"
            >
              Cancel
            </MUIButton>,
          ]
        : showEditButton
          ? [
              <MUIButton
                disabled={chartContext.isUpdating}
                onClick={() => setEditMode(true)}
                size="small"
              >
                Edit
              </MUIButton>,
            ]
          : []),
    ];
  };

  return (
    <Card
      renderContent={renderContent}
      getActions={getActions}
      getTitle={() => title}
      getIcon={() => icon}
    />
  );
}

CardChartContact.meta = { className: className, title: title, icon: icon };
